import type { ActionContext, ActionHandler, EffectRecord } from '../types'

async function toggleField(
  ctx: ActionContext,
  table: string,
  rowId: string,
  field: string,
  newValue: boolean,
  labelPrefix: string,
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: old } = await ctx.supabase
    .from(table)
    .select('*')
    .eq('id', rowId)
    .single() as { data: Record<string, any> | null }

  if (!old) throw new Error(`${table} row not found`)

  const { error } = await ctx.supabase
    .from(table)
    .update({ [field]: newValue })
    .eq('id', rowId)

  if (error) throw error

  const effects: EffectRecord[] = [{
    character_id: ctx.characterId,
    target_table: table,
    target_row_id: rowId,
    target_field: field,
    operation: 'UPDATE',
    old_value: old[field],
    new_value: newValue,
  }]

  const itemName = old.item_name ?? old.weapon_name ?? 'item'

  return { effects, label: `${labelPrefix} ${itemName}` }
}

export const handleEquipArmour: ActionHandler<'EQUIP_ARMOUR'> = (ctx, payload) =>
  toggleField(ctx, 'character_armour', payload.armour_id, 'is_worn', true, 'Equip')

export const handleUnequipArmour: ActionHandler<'UNEQUIP_ARMOUR'> = (ctx, payload) =>
  toggleField(ctx, 'character_armour', payload.armour_id, 'is_worn', false, 'Remove')

export const handleEquipWeapon: ActionHandler<'EQUIP_WEAPON'> = async (ctx, payload) => {
  const { data: old } = await ctx.supabase.from('character_weapons').select('*').eq('id', payload.weapon_id).single() as { data: Record<string, any> | null }
  if (!old) throw new Error('Weapon not found')

  // Find which hands are already occupied by other held weapons
  const { data: heldWeapons } = await ctx.supabase
    .from('character_weapons')
    .select('held_in, two_handed')
    .eq('character_id', ctx.characterId)
    .not('held_in', 'is', null)
    .neq('id', payload.weapon_id) as { data: Array<{ held_in: string; two_handed: boolean }> | null }

  const rightOccupied = heldWeapons?.some(w => w.held_in === 'right' || w.two_handed) ?? false
  const leftOccupied = heldWeapons?.some(w => w.held_in === 'left' || w.two_handed) ?? false

  const heldIn = !rightOccupied ? 'right' : !leftOccupied ? 'left' : null

  const { error } = await ctx.supabase.from('character_weapons')
    .update({ is_equipped: true, held_in: heldIn })
    .eq('id', payload.weapon_id)
  if (error) throw error

  const effects: EffectRecord[] = [
    { character_id: ctx.characterId, target_table: 'character_weapons', target_row_id: payload.weapon_id, target_field: 'is_equipped', operation: 'UPDATE', old_value: old.is_equipped, new_value: true },
    { character_id: ctx.characterId, target_table: 'character_weapons', target_row_id: payload.weapon_id, target_field: 'held_in', operation: 'UPDATE', old_value: old.held_in, new_value: heldIn },
  ]

  const destination = heldIn ? `${heldIn} hand` : 'belt'
  return { effects, label: `Pick up ${old.weapon_name} (${destination})` }
}

export const handleUnequipWeapon: ActionHandler<'UNEQUIP_WEAPON'> = (ctx, payload) =>
  toggleField(ctx, 'character_weapons', payload.weapon_id, 'is_equipped', false, 'Stow')

export const handleAbandonWeapon: ActionHandler<'ABANDON_WEAPON'> = async (ctx, payload) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: old } = await ctx.supabase.from('character_weapons').select('*').eq('id', payload.weapon_id).single() as { data: Record<string, any> | null }
  if (!old) throw new Error('Weapon not found')
  await ctx.supabase.from('character_weapons').delete().eq('id', payload.weapon_id)
  return {
    effects: [{ character_id: ctx.characterId, target_table: 'character_weapons', target_row_id: payload.weapon_id, target_field: '*', operation: 'DELETE' as const, old_value: old, new_value: null }],
    label: `Abandon: ${old.weapon_name}`,
  }
}

export const handleDrawWeaponToHand: ActionHandler<'DRAW_WEAPON_TO_HAND'> = async (ctx, payload) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: old } = await ctx.supabase.from('character_weapons').select('*').eq('id', payload.weapon_id).single() as { data: Record<string, any> | null }
  if (!old) throw new Error('Weapon not found')
  const { error } = await ctx.supabase.from('character_weapons')
    .update({ held_in: payload.hand, is_equipped: true })
    .eq('id', payload.weapon_id)
  if (error) throw error
  return {
    effects: [
      { character_id: ctx.characterId, target_table: 'character_weapons', target_row_id: payload.weapon_id, target_field: 'held_in', operation: 'UPDATE' as const, old_value: old.held_in, new_value: payload.hand },
      { character_id: ctx.characterId, target_table: 'character_weapons', target_row_id: payload.weapon_id, target_field: 'is_equipped', operation: 'UPDATE' as const, old_value: old.is_equipped, new_value: true },
    ],
    label: `Draw ${old.weapon_name} to ${payload.hand} hand`,
  }
}

export const handleReleaseWeaponFromHand: ActionHandler<'RELEASE_WEAPON_FROM_HAND'> = async (ctx, payload) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: old } = await ctx.supabase.from('character_weapons').select('*').eq('id', payload.weapon_id).single() as { data: Record<string, any> | null }
  if (!old) throw new Error('Weapon not found')
  const { error } = await ctx.supabase.from('character_weapons')
    .update({ held_in: null, two_handed: false })
    .eq('id', payload.weapon_id)
  if (error) throw error
  return {
    effects: [
      { character_id: ctx.characterId, target_table: 'character_weapons', target_row_id: payload.weapon_id, target_field: 'held_in', operation: 'UPDATE' as const, old_value: old.held_in, new_value: null },
      { character_id: ctx.characterId, target_table: 'character_weapons', target_row_id: payload.weapon_id, target_field: 'two_handed', operation: 'UPDATE' as const, old_value: old.two_handed, new_value: false },
    ],
    label: `Sheathe ${old.weapon_name}`,
  }
}

export const handleDropWeaponFromHand: ActionHandler<'DROP_WEAPON_FROM_HAND'> = async (ctx, payload) => {
  const { data: old } = await ctx.supabase.from('character_weapons').select('*').eq('id', payload.weapon_id).single() as { data: Record<string, any> | null }
  if (!old) throw new Error('Weapon not found')
  const { error } = await ctx.supabase.from('character_weapons')
    .update({ held_in: null, two_handed: false, is_equipped: false })
    .eq('id', payload.weapon_id)
  if (error) throw error
  return {
    effects: [
      { character_id: ctx.characterId, target_table: 'character_weapons', target_row_id: payload.weapon_id, target_field: 'held_in', operation: 'UPDATE' as const, old_value: old.held_in, new_value: null },
      { character_id: ctx.characterId, target_table: 'character_weapons', target_row_id: payload.weapon_id, target_field: 'two_handed', operation: 'UPDATE' as const, old_value: old.two_handed, new_value: false },
      { character_id: ctx.characterId, target_table: 'character_weapons', target_row_id: payload.weapon_id, target_field: 'is_equipped', operation: 'UPDATE' as const, old_value: old.is_equipped, new_value: false },
    ],
    label: `Drop ${old.weapon_name}`,
  }
}

export const handleGripWeaponTwoHanded: ActionHandler<'GRIP_WEAPON_TWO_HANDED'> = async (ctx, payload) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: old } = await ctx.supabase.from('character_weapons').select('*').eq('id', payload.weapon_id).single() as { data: Record<string, any> | null }
  if (!old) throw new Error('Weapon not found')
  const { error } = await ctx.supabase.from('character_weapons')
    .update({ two_handed: true })
    .eq('id', payload.weapon_id)
  if (error) throw error
  return {
    effects: [
      { character_id: ctx.characterId, target_table: 'character_weapons', target_row_id: payload.weapon_id, target_field: 'two_handed', operation: 'UPDATE' as const, old_value: old.two_handed, new_value: true },
    ],
    label: `Grip ${old.weapon_name} two-handed`,
  }
}

export const handleAbandonArmour: ActionHandler<'ABANDON_ARMOUR'> = async (ctx, payload) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: old } = await ctx.supabase.from('character_armour').select('*').eq('id', payload.armour_id).single() as { data: Record<string, any> | null }
  if (!old) throw new Error('Armour not found')
  await ctx.supabase.from('character_armour').delete().eq('id', payload.armour_id)
  return {
    effects: [{ character_id: ctx.characterId, target_table: 'character_armour', target_row_id: payload.armour_id, target_field: '*', operation: 'DELETE' as const, old_value: old, new_value: null }],
    label: `Abandon: ${old.item_name}`,
  }
}
